/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Recap
 * OpenAPI spec version: 1
 */
import * as zod from 'zod';

import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useApiFetch } from '../client.ts';
import type { ErrorType } from '../client.ts';
import { useMutationBehavior } from '../mutations.ts';
import { useQueryBehavior } from '../queries.ts';
import {
    Client,
    Employee,
    EmployeeClientDetail,
    EmployeeInvitation,
    EmployeeInvitationPreview,
    Project,
    PublicProject,
    Workspace,
} from './schemas.ts';
import type {
    ClientInput,
    ClientTeamUpdate,
    EmailAddressUpdate,
    EmployeeInvitationInput,
    EmployeeUpdate,
    ErrorResponse,
    ProjectClientUpdate,
    ProjectCreate,
    ProjectEmployeesUpdate,
    ProjectInput,
    ProjectTeamUpdate,
    StarUpdate,
    UploadClientLogoBody,
    UploadProjectLogoBody,
} from './schemas.ts';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
    const result = { queryKey } as T & { queryKey: K };
    for (const key of Object.keys(query)) {
        // The explicit queryKey always wins, matching the previous
        // `{ ...query, queryKey }` spread where it was set last.
        if (key === 'queryKey') continue;
        Object.defineProperty(result, key, {
            enumerable: true,
            configurable: true,
            get: () => (query as Record<string, unknown>)[key],
        });
    }
    return result;
};

export const getGetEmployeeInvitationPreviewUrl = (token: string | null) => {
    return `/employee-invitations/${token}`;
};

/**
 * @summary Get Employee Invitation Preview
 */
export const useGetEmployeeInvitationPreviewHook = (): ((
    token: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeInvitationPreview>) => {
    const customFetcher = useApiFetch();
    return (token: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetEmployeeInvitationPreviewUrl(token), {
            ...options,
            method: 'GET',

            schema: EmployeeInvitationPreview,
        });
    };
};

export const getGetEmployeeInvitationPreviewQueryKey = (token: string | null) => {
    return ['employee-invitations', token] as const;
};

export const useGetEmployeeInvitationPreviewQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetEmployeeInvitationPreviewQueryKey(token);

    const getEmployeeInvitationPreview = useGetEmployeeInvitationPreviewHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>> = ({
        signal,
    }) => getEmployeeInvitationPreview(token, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions },
        { token },
        {
            url: `/employee-invitations/${token}`,
            operationId: 'get_employee_invitation_preview',
            operationName: 'getEmployeeInvitationPreview',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEmployeeInvitationPreviewQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>
>;
export type GetEmployeeInvitationPreviewQueryError = ErrorType<ErrorResponse>;

export function useGetEmployeeInvitationPreview<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeInvitationPreview<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeInvitationPreview<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Employee Invitation Preview
 */

export function useGetEmployeeInvitationPreview<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeInvitationPreviewHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetEmployeeInvitationPreviewQueryOptions(token, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getAcceptEmployeeInvitationUrl = (token: string) => {
    return `/employee-invitations/${token}/accept`;
};

/**
 * @summary Accept Employee Invitation
 */
export const useAcceptEmployeeInvitationHook = (): ((
    token: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Workspace>) => {
    const customFetcher = useApiFetch();
    return (token: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getAcceptEmployeeInvitationUrl(token), {
            ...options,
            method: 'POST',

            schema: Workspace,
        });
    };
};

export const getAcceptEmployeeInvitationMutationKey = () => ['acceptEmployeeInvitation'] as const;

export const useAcceptEmployeeInvitationMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useAcceptEmployeeInvitationHook>>>,
        TError,
        AcceptEmployeeInvitationMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useAcceptEmployeeInvitationHook>>>,
    TError,
    AcceptEmployeeInvitationMutationVariables,
    TContext
> => {
    const mutationKey = getAcceptEmployeeInvitationMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const acceptEmployeeInvitation = useAcceptEmployeeInvitationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useAcceptEmployeeInvitationHook>>>,
        AcceptEmployeeInvitationMutationVariables
    > = (props) => {
        const { token } = props ?? {};

        return acceptEmployeeInvitation(token, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/employee-invitations/{token}/accept` },
        { operationId: 'accept_employee_invitation', operationName: 'acceptEmployeeInvitation' }
    );

    return customOptions;
};

export type AcceptEmployeeInvitationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useAcceptEmployeeInvitationHook>>>
>;

export type AcceptEmployeeInvitationMutationError = ErrorType<ErrorResponse>;
export type AcceptEmployeeInvitationMutationVariables = { token: string };

/**
 * @summary Accept Employee Invitation
 */
export const useAcceptEmployeeInvitation = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useAcceptEmployeeInvitationHook>>>,
            TError,
            AcceptEmployeeInvitationMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useAcceptEmployeeInvitationHook>>>,
    TError,
    AcceptEmployeeInvitationMutationVariables,
    TContext
> => {
    return useMutation(useAcceptEmployeeInvitationMutationOptions(options), queryClient);
};
export const getGetPublicProjectUrl = (token: string | null) => {
    return `/public/projects/${token}`;
};

/**
 * @summary Get Public Project
 */
export const useGetPublicProjectHook = (): ((
    token: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<PublicProject>) => {
    const customFetcher = useApiFetch();
    return (token: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetPublicProjectUrl(token), {
            ...options,
            method: 'GET',

            schema: PublicProject,
        });
    };
};

export const getGetPublicProjectQueryKey = (token: string | null) => {
    return ['public', 'projects', token] as const;
};

export const useGetPublicProjectQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPublicProjectQueryKey(token);

    const getPublicProject = useGetPublicProjectHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>> = ({ signal }) =>
        getPublicProject(token, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions },
        { token },
        { url: `/public/projects/${token}`, operationId: 'get_public_project', operationName: 'getPublicProject' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPublicProjectQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>>;
export type GetPublicProjectQueryError = ErrorType<ErrorResponse>;

export function useGetPublicProject<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicProject<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicProject<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Public Project
 */

export function useGetPublicProject<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetPublicProjectQueryOptions(token, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetPublicClientLogoUrl = (token: string | null) => {
    return `/public/projects/${token}/client/logo`;
};

/**
 * @summary Get Public Client Logo
 */
export const useGetPublicClientLogoHook = (): ((
    token: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (token: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetPublicClientLogoUrl(token), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetPublicClientLogoQueryKey = (token: string | null) => {
    return ['public', 'projects', token, 'client', 'logo'] as const;
};

export const useGetPublicClientLogoQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPublicClientLogoQueryKey(token);

    const getPublicClientLogo = useGetPublicClientLogoHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>> = ({ signal }) =>
        getPublicClientLogo(token, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions },
        { token },
        {
            url: `/public/projects/${token}/client/logo`,
            operationId: 'get_public_client_logo',
            operationName: 'getPublicClientLogo',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPublicClientLogoQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>
>;
export type GetPublicClientLogoQueryError = ErrorType<ErrorResponse>;

export function useGetPublicClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Public Client Logo
 */

export function useGetPublicClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetPublicClientLogoQueryOptions(token, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetPublicProjectLogoUrl = (token: string | null) => {
    return `/public/projects/${token}/logo`;
};

/**
 * @summary Get Public Project Logo
 */
export const useGetPublicProjectLogoHook = (): ((
    token: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (token: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetPublicProjectLogoUrl(token), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetPublicProjectLogoQueryKey = (token: string | null) => {
    return ['public', 'projects', token, 'logo'] as const;
};

export const useGetPublicProjectLogoQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetPublicProjectLogoQueryKey(token);

    const getPublicProjectLogo = useGetPublicProjectLogoHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>> = ({ signal }) =>
        getPublicProjectLogo(token, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: token !== null && token !== undefined, ...queryOptions },
        { token },
        {
            url: `/public/projects/${token}/logo`,
            operationId: 'get_public_project_logo',
            operationName: 'getPublicProjectLogo',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPublicProjectLogoQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>
>;
export type GetPublicProjectLogoQueryError = ErrorType<ErrorResponse>;

export function useGetPublicProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPublicProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Public Project Logo
 */

export function useGetPublicProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    token: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPublicProjectLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetPublicProjectLogoQueryOptions(token, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getListClientsUrl = () => {
    return `/workspace/clients`;
};

/**
 * @summary List Clients
 */
export const useListClientsHook = (): ((
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client[]>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListClientsUrl(), {
            ...options,
            method: 'GET',

            schema: zod.array(Client),
        });
    };
};

export const getListClientsQueryKey = () => {
    return ['workspace', 'clients'] as const;
};

export const useListClientsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>, TError, TData>>;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListClientsQueryKey();

    const listClients = useListClientsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>> = ({ signal }) =>
        listClients({ signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        {},
        { url: `/workspace/clients`, operationId: 'list_clients', operationName: 'listClients' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListClientsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>>;
export type ListClientsQueryError = ErrorType<ErrorResponse>;

export function useListClients<
    TData = Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListClients<
    TData = Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListClients<
    TData = Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Clients
 */

export function useListClients<
    TData = Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListClientsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListClientsQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateClientUrl = () => {
    return `/workspace/clients`;
};

/**
 * @summary Create Client
 */
export const useCreateClientHook = (): ((
    clientInput: ClientInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (clientInput: ClientInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateClientUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(clientInput),
            schema: Client,
        });
    };
};

export const getCreateClientMutationKey = () => ['createClient'] as const;

export const useCreateClientMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
        TError,
        CreateClientMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
    TError,
    CreateClientMutationVariables,
    TContext
> => {
    const mutationKey = getCreateClientMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createClient = useCreateClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
        CreateClientMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return createClient(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients` },
        { operationId: 'create_client', operationName: 'createClient' }
    );

    return customOptions;
};

export type CreateClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>>;
export type CreateClientMutationBody = ClientInput;
export type CreateClientMutationError = ErrorType<ErrorResponse>;
export type CreateClientMutationVariables = { data: ClientInput };

/**
 * @summary Create Client
 */
export const useCreateClient = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
            TError,
            CreateClientMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateClientHook>>>,
    TError,
    CreateClientMutationVariables,
    TContext
> => {
    return useMutation(useCreateClientMutationOptions(options), queryClient);
};
export const getGetClientUrl = (clientId: string | null) => {
    return `/workspace/clients/${clientId}`;
};

/**
 * @summary Get Client
 */
export const useGetClientHook = (): ((
    clientId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const customFetcher = useApiFetch();
    return (clientId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetClientUrl(clientId), {
            ...options,
            method: 'GET',

            schema: Client,
        });
    };
};

export const getGetClientQueryKey = (clientId: string | null) => {
    return ['workspace', 'clients', clientId] as const;
};

export const useGetClientQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientQueryKey(clientId);

    const getClient = useGetClientHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>> = ({ signal }) =>
        getClient(clientId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: clientId !== null && clientId !== undefined, ...queryOptions },
        { clientId },
        { url: `/workspace/clients/${clientId}`, operationId: 'get_client', operationName: 'getClient' }
    );

    return customOptions as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>, TError, TData> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };
};

export type GetClientQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>>;
export type GetClientQueryError = ErrorType<ErrorResponse>;

export function useGetClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Client
 */

export function useGetClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientQueryOptions(clientId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateClientUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}`;
};

/**
 * @summary Update Client
 */
export const useUpdateClientHook = (): ((
    clientId: string,
    clientInput: ClientInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (clientId: string, clientInput: ClientInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getUpdateClientUrl(clientId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(clientInput),
            schema: Client,
        });
    };
};

export const getUpdateClientMutationKey = () => ['updateClient'] as const;

export const useUpdateClientMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
        TError,
        UpdateClientMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
    TError,
    UpdateClientMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateClientMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateClient = useUpdateClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
        UpdateClientMutationVariables
    > = (props) => {
        const { clientId, data } = props ?? {};

        return updateClient(clientId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}` },
        { operationId: 'update_client', operationName: 'updateClient' }
    );

    return customOptions;
};

export type UpdateClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>>;
export type UpdateClientMutationBody = ClientInput;
export type UpdateClientMutationError = ErrorType<ErrorResponse>;
export type UpdateClientMutationVariables = { clientId: string; data: ClientInput };

/**
 * @summary Update Client
 */
export const useUpdateClient = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
            TError,
            UpdateClientMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientHook>>>,
    TError,
    UpdateClientMutationVariables,
    TContext
> => {
    return useMutation(useUpdateClientMutationOptions(options), queryClient);
};
export const getDeleteClientUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}`;
};

/**
 * @summary Delete Client
 */
export const useDeleteClientHook = (): ((
    clientId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (clientId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteClientUrl(clientId), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getDeleteClientMutationKey = () => ['deleteClient'] as const;

export const useDeleteClientMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
        TError,
        DeleteClientMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
    TError,
    DeleteClientMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteClientMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteClient = useDeleteClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
        DeleteClientMutationVariables
    > = (props) => {
        const { clientId } = props ?? {};

        return deleteClient(clientId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}` },
        { operationId: 'delete_client', operationName: 'deleteClient' }
    );

    return customOptions;
};

export type DeleteClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>>;

export type DeleteClientMutationError = ErrorType<ErrorResponse>;
export type DeleteClientMutationVariables = { clientId: string };

/**
 * @summary Delete Client
 */
export const useDeleteClient = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
            TError,
            DeleteClientMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientHook>>>,
    TError,
    DeleteClientMutationVariables,
    TContext
> => {
    return useMutation(useDeleteClientMutationOptions(options), queryClient);
};
export const getStarClientUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}/star`;
};

/**
 * @summary Star Client
 */
export const useStarClientHook = (): ((
    clientId: string,
    starUpdate: StarUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (clientId: string, starUpdate: StarUpdate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getStarClientUrl(clientId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(starUpdate),
            schema: Client,
        });
    };
};

export const getStarClientMutationKey = () => ['starClient'] as const;

export const useStarClientMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useStarClientHook>>>,
        TError,
        StarClientMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useStarClientHook>>>,
    TError,
    StarClientMutationVariables,
    TContext
> => {
    const mutationKey = getStarClientMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const starClient = useStarClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useStarClientHook>>>,
        StarClientMutationVariables
    > = (props) => {
        const { clientId, data } = props ?? {};

        return starClient(clientId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}/star` },
        { operationId: 'star_client', operationName: 'starClient' }
    );

    return customOptions;
};

export type StarClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useStarClientHook>>>>;
export type StarClientMutationBody = StarUpdate;
export type StarClientMutationError = ErrorType<ErrorResponse>;
export type StarClientMutationVariables = { clientId: string; data: StarUpdate };

/**
 * @summary Star Client
 */
export const useStarClient = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useStarClientHook>>>,
            TError,
            StarClientMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useStarClientHook>>>,
    TError,
    StarClientMutationVariables,
    TContext
> => {
    return useMutation(useStarClientMutationOptions(options), queryClient);
};
export const getUpdateClientTeamUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}/team`;
};

/**
 * @summary Update Client Team
 */
export const useUpdateClientTeamHook = (): ((
    clientId: string,
    clientTeamUpdate: ClientTeamUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        clientId: string,
        clientTeamUpdate: ClientTeamUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateClientTeamUrl(clientId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(clientTeamUpdate),
            schema: Client,
        });
    };
};

export const getUpdateClientTeamMutationKey = () => ['updateClientTeam'] as const;

export const useUpdateClientTeamMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientTeamHook>>>,
        TError,
        UpdateClientTeamMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientTeamHook>>>,
    TError,
    UpdateClientTeamMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateClientTeamMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateClientTeam = useUpdateClientTeamHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientTeamHook>>>,
        UpdateClientTeamMutationVariables
    > = (props) => {
        const { clientId, data } = props ?? {};

        return updateClientTeam(clientId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}/team` },
        { operationId: 'update_client_team', operationName: 'updateClientTeam' }
    );

    return customOptions;
};

export type UpdateClientTeamMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientTeamHook>>>
>;
export type UpdateClientTeamMutationBody = ClientTeamUpdate;
export type UpdateClientTeamMutationError = ErrorType<ErrorResponse>;
export type UpdateClientTeamMutationVariables = { clientId: string; data: ClientTeamUpdate };

/**
 * @summary Update Client Team
 */
export const useUpdateClientTeam = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateClientTeamHook>>>,
            TError,
            UpdateClientTeamMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientTeamHook>>>,
    TError,
    UpdateClientTeamMutationVariables,
    TContext
> => {
    return useMutation(useUpdateClientTeamMutationOptions(options), queryClient);
};
export const getUploadClientLogoUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}/logo`;
};

/**
 * @summary Upload Client Logo
 */
export const useUploadClientLogoHook = (): ((
    clientId: string,
    uploadClientLogoBody: UploadClientLogoBody,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const customFetcher = useApiFetch();
    return (
        clientId: string,
        uploadClientLogoBody: UploadClientLogoBody,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        const formData = new FormData();
        formData.append(`file`, uploadClientLogoBody.file);

        return customFetcher(getUploadClientLogoUrl(clientId), {
            ...options,
            method: 'POST',
            body: formData,
            schema: Client,
        });
    };
};

export const getUploadClientLogoMutationKey = () => ['uploadClientLogo'] as const;

export const useUploadClientLogoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadClientLogoHook>>>,
        TError,
        UploadClientLogoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadClientLogoHook>>>,
    TError,
    UploadClientLogoMutationVariables,
    TContext
> => {
    const mutationKey = getUploadClientLogoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const uploadClientLogo = useUploadClientLogoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadClientLogoHook>>>,
        UploadClientLogoMutationVariables
    > = (props) => {
        const { clientId, data } = props ?? {};

        return uploadClientLogo(clientId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}/logo` },
        { operationId: 'upload_client_logo', operationName: 'uploadClientLogo' }
    );

    return customOptions;
};

export type UploadClientLogoMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadClientLogoHook>>>
>;
export type UploadClientLogoMutationBody = UploadClientLogoBody;
export type UploadClientLogoMutationError = ErrorType<ErrorResponse>;
export type UploadClientLogoMutationVariables = { clientId: string; data: UploadClientLogoBody };

/**
 * @summary Upload Client Logo
 */
export const useUploadClientLogo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadClientLogoHook>>>,
            TError,
            UploadClientLogoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadClientLogoHook>>>,
    TError,
    UploadClientLogoMutationVariables,
    TContext
> => {
    return useMutation(useUploadClientLogoMutationOptions(options), queryClient);
};
export const getDeleteClientLogoUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}/logo`;
};

/**
 * @summary Delete Client Logo
 */
export const useDeleteClientLogoHook = (): ((
    clientId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const customFetcher = useApiFetch();
    return (clientId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteClientLogoUrl(clientId), {
            ...options,
            method: 'DELETE',

            schema: Client,
        });
    };
};

export const getDeleteClientLogoMutationKey = () => ['deleteClientLogo'] as const;

export const useDeleteClientLogoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientLogoHook>>>,
        TError,
        DeleteClientLogoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientLogoHook>>>,
    TError,
    DeleteClientLogoMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteClientLogoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteClientLogo = useDeleteClientLogoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientLogoHook>>>,
        DeleteClientLogoMutationVariables
    > = (props) => {
        const { clientId } = props ?? {};

        return deleteClientLogo(clientId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}/logo` },
        { operationId: 'delete_client_logo', operationName: 'deleteClientLogo' }
    );

    return customOptions;
};

export type DeleteClientLogoMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientLogoHook>>>
>;

export type DeleteClientLogoMutationError = ErrorType<ErrorResponse>;
export type DeleteClientLogoMutationVariables = { clientId: string };

/**
 * @summary Delete Client Logo
 */
export const useDeleteClientLogo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteClientLogoHook>>>,
            TError,
            DeleteClientLogoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientLogoHook>>>,
    TError,
    DeleteClientLogoMutationVariables,
    TContext
> => {
    return useMutation(useDeleteClientLogoMutationOptions(options), queryClient);
};
export const getGetClientLogoUrl = (clientId: string | null) => {
    return `/workspace/clients/${clientId}/logo`;
};

/**
 * @summary Get Client Logo
 */
export const useGetClientLogoHook = (): ((
    clientId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (clientId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetClientLogoUrl(clientId), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetClientLogoQueryKey = (clientId: string | null) => {
    return ['workspace', 'clients', clientId, 'logo'] as const;
};

export const useGetClientLogoQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientLogoQueryKey(clientId);

    const getClientLogo = useGetClientLogoHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>> = ({ signal }) =>
        getClientLogo(clientId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: clientId !== null && clientId !== undefined, ...queryOptions },
        { clientId },
        { url: `/workspace/clients/${clientId}/logo`, operationId: 'get_client_logo', operationName: 'getClientLogo' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientLogoQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>>;
export type GetClientLogoQueryError = ErrorType<ErrorResponse>;

export function useGetClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Client Logo
 */

export function useGetClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientLogoHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientLogoQueryOptions(clientId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getListAllProjectsUrl = () => {
    return `/workspace/projects`;
};

/**
 * @summary List All Projects
 */
export const useListAllProjectsHook = (): ((
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project[]>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListAllProjectsUrl(), {
            ...options,
            method: 'GET',

            schema: zod.array(Project),
        });
    };
};

export const getListAllProjectsQueryKey = () => {
    return ['workspace', 'projects'] as const;
};

export const useListAllProjectsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>, TError, TData>>;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListAllProjectsQueryKey();

    const listAllProjects = useListAllProjectsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>> = ({ signal }) =>
        listAllProjects({ signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        {},
        { url: `/workspace/projects`, operationId: 'list_all_projects', operationName: 'listAllProjects' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAllProjectsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>>;
export type ListAllProjectsQueryError = ErrorType<ErrorResponse>;

export function useListAllProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAllProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAllProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List All Projects
 */

export function useListAllProjects<
    TData = Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListAllProjectsHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListAllProjectsQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateProjectUrl = () => {
    return `/workspace/projects`;
};

/**
 * @summary Create Project
 */
export const useCreateProjectHook = (): ((
    projectCreate: ProjectCreate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (projectCreate: ProjectCreate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreateProjectUrl(), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(projectCreate),
            schema: Project,
        });
    };
};

export const getCreateProjectMutationKey = () => ['createProject'] as const;

export const useCreateProjectMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateProjectHook>>>,
        TError,
        CreateProjectMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateProjectHook>>>,
    TError,
    CreateProjectMutationVariables,
    TContext
> => {
    const mutationKey = getCreateProjectMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createProject = useCreateProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateProjectHook>>>,
        CreateProjectMutationVariables
    > = (props) => {
        const { data } = props ?? {};

        return createProject(data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects` },
        { operationId: 'create_project', operationName: 'createProject' }
    );

    return customOptions;
};

export type CreateProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateProjectHook>>>>;
export type CreateProjectMutationBody = ProjectCreate;
export type CreateProjectMutationError = ErrorType<ErrorResponse>;
export type CreateProjectMutationVariables = { data: ProjectCreate };

/**
 * @summary Create Project
 */
export const useCreateProject = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateProjectHook>>>,
            TError,
            CreateProjectMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateProjectHook>>>,
    TError,
    CreateProjectMutationVariables,
    TContext
> => {
    return useMutation(useCreateProjectMutationOptions(options), queryClient);
};
export const getUpdateProjectClientUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/client`;
};

/**
 * @summary Update Project Client
 */
export const useUpdateProjectClientHook = (): ((
    projectId: string,
    projectClientUpdate: ProjectClientUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        projectId: string,
        projectClientUpdate: ProjectClientUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateProjectClientUrl(projectId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(projectClientUpdate),
            schema: Project,
        });
    };
};

export const getUpdateProjectClientMutationKey = () => ['updateProjectClient'] as const;

export const useUpdateProjectClientMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectClientHook>>>,
        TError,
        UpdateProjectClientMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectClientHook>>>,
    TError,
    UpdateProjectClientMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateProjectClientMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateProjectClient = useUpdateProjectClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectClientHook>>>,
        UpdateProjectClientMutationVariables
    > = (props) => {
        const { projectId, data } = props ?? {};

        return updateProjectClient(projectId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/client` },
        { operationId: 'update_project_client', operationName: 'updateProjectClient' }
    );

    return customOptions;
};

export type UpdateProjectClientMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectClientHook>>>
>;
export type UpdateProjectClientMutationBody = ProjectClientUpdate;
export type UpdateProjectClientMutationError = ErrorType<ErrorResponse>;
export type UpdateProjectClientMutationVariables = { projectId: string; data: ProjectClientUpdate };

/**
 * @summary Update Project Client
 */
export const useUpdateProjectClient = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateProjectClientHook>>>,
            TError,
            UpdateProjectClientMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectClientHook>>>,
    TError,
    UpdateProjectClientMutationVariables,
    TContext
> => {
    return useMutation(useUpdateProjectClientMutationOptions(options), queryClient);
};
export const getUpdateProjectUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}`;
};

/**
 * @summary Update Project
 */
export const useUpdateProjectHook = (): ((
    projectId: string,
    projectInput: ProjectInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (projectId: string, projectInput: ProjectInput, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getUpdateProjectUrl(projectId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(projectInput),
            schema: Project,
        });
    };
};

export const getUpdateProjectMutationKey = () => ['updateProject'] as const;

export const useUpdateProjectMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectHook>>>,
        TError,
        UpdateProjectMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectHook>>>,
    TError,
    UpdateProjectMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateProjectMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateProject = useUpdateProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectHook>>>,
        UpdateProjectMutationVariables
    > = (props) => {
        const { projectId, data } = props ?? {};

        return updateProject(projectId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}` },
        { operationId: 'update_project', operationName: 'updateProject' }
    );

    return customOptions;
};

export type UpdateProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateProjectHook>>>>;
export type UpdateProjectMutationBody = ProjectInput;
export type UpdateProjectMutationError = ErrorType<ErrorResponse>;
export type UpdateProjectMutationVariables = { projectId: string; data: ProjectInput };

/**
 * @summary Update Project
 */
export const useUpdateProject = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateProjectHook>>>,
            TError,
            UpdateProjectMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectHook>>>,
    TError,
    UpdateProjectMutationVariables,
    TContext
> => {
    return useMutation(useUpdateProjectMutationOptions(options), queryClient);
};
export const getDeleteProjectUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}`;
};

/**
 * @summary Delete Project
 */
export const useDeleteProjectHook = (): ((
    projectId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (projectId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteProjectUrl(projectId), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getDeleteProjectMutationKey = () => ['deleteProject'] as const;

export const useDeleteProjectMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
        TError,
        DeleteProjectMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
    TError,
    DeleteProjectMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteProjectMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteProject = useDeleteProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
        DeleteProjectMutationVariables
    > = (props) => {
        const { projectId } = props ?? {};

        return deleteProject(projectId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}` },
        { operationId: 'delete_project', operationName: 'deleteProject' }
    );

    return customOptions;
};

export type DeleteProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>>;

export type DeleteProjectMutationError = ErrorType<ErrorResponse>;
export type DeleteProjectMutationVariables = { projectId: string };

/**
 * @summary Delete Project
 */
export const useDeleteProject = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
            TError,
            DeleteProjectMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteProjectHook>>>,
    TError,
    DeleteProjectMutationVariables,
    TContext
> => {
    return useMutation(useDeleteProjectMutationOptions(options), queryClient);
};
export const getStarProjectUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/star`;
};

/**
 * @summary Star Project
 */
export const useStarProjectHook = (): ((
    projectId: string,
    starUpdate: StarUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (projectId: string, starUpdate: StarUpdate, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getStarProjectUrl(projectId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(starUpdate),
            schema: Project,
        });
    };
};

export const getStarProjectMutationKey = () => ['starProject'] as const;

export const useStarProjectMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useStarProjectHook>>>,
        TError,
        StarProjectMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useStarProjectHook>>>,
    TError,
    StarProjectMutationVariables,
    TContext
> => {
    const mutationKey = getStarProjectMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const starProject = useStarProjectHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useStarProjectHook>>>,
        StarProjectMutationVariables
    > = (props) => {
        const { projectId, data } = props ?? {};

        return starProject(projectId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/star` },
        { operationId: 'star_project', operationName: 'starProject' }
    );

    return customOptions;
};

export type StarProjectMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useStarProjectHook>>>>;
export type StarProjectMutationBody = StarUpdate;
export type StarProjectMutationError = ErrorType<ErrorResponse>;
export type StarProjectMutationVariables = { projectId: string; data: StarUpdate };

/**
 * @summary Star Project
 */
export const useStarProject = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useStarProjectHook>>>,
            TError,
            StarProjectMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useStarProjectHook>>>,
    TError,
    StarProjectMutationVariables,
    TContext
> => {
    return useMutation(useStarProjectMutationOptions(options), queryClient);
};
export const getUpdateProjectTeamUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/team`;
};

/**
 * @summary Update Project Team
 */
export const useUpdateProjectTeamHook = (): ((
    projectId: string,
    projectTeamUpdate: ProjectTeamUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        projectId: string,
        projectTeamUpdate: ProjectTeamUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateProjectTeamUrl(projectId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(projectTeamUpdate),
            schema: Project,
        });
    };
};

export const getUpdateProjectTeamMutationKey = () => ['updateProjectTeam'] as const;

export const useUpdateProjectTeamMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectTeamHook>>>,
        TError,
        UpdateProjectTeamMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectTeamHook>>>,
    TError,
    UpdateProjectTeamMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateProjectTeamMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateProjectTeam = useUpdateProjectTeamHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectTeamHook>>>,
        UpdateProjectTeamMutationVariables
    > = (props) => {
        const { projectId, data } = props ?? {};

        return updateProjectTeam(projectId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/team` },
        { operationId: 'update_project_team', operationName: 'updateProjectTeam' }
    );

    return customOptions;
};

export type UpdateProjectTeamMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectTeamHook>>>
>;
export type UpdateProjectTeamMutationBody = ProjectTeamUpdate;
export type UpdateProjectTeamMutationError = ErrorType<ErrorResponse>;
export type UpdateProjectTeamMutationVariables = { projectId: string; data: ProjectTeamUpdate };

/**
 * @summary Update Project Team
 */
export const useUpdateProjectTeam = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateProjectTeamHook>>>,
            TError,
            UpdateProjectTeamMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectTeamHook>>>,
    TError,
    UpdateProjectTeamMutationVariables,
    TContext
> => {
    return useMutation(useUpdateProjectTeamMutationOptions(options), queryClient);
};
export const getUpdateProjectEmployeesUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/employees`;
};

/**
 * @summary Update Project Employees
 */
export const useUpdateProjectEmployeesHook = (): ((
    projectId: string,
    projectEmployeesUpdate: ProjectEmployeesUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Employee[]>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        projectId: string,
        projectEmployeesUpdate: ProjectEmployeesUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateProjectEmployeesUrl(projectId), {
            ...options,
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(projectEmployeesUpdate),
            schema: zod.array(Employee),
        });
    };
};

export const getUpdateProjectEmployeesMutationKey = () => ['updateProjectEmployees'] as const;

export const useUpdateProjectEmployeesMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmployeesHook>>>,
        TError,
        UpdateProjectEmployeesMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmployeesHook>>>,
    TError,
    UpdateProjectEmployeesMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateProjectEmployeesMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateProjectEmployees = useUpdateProjectEmployeesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmployeesHook>>>,
        UpdateProjectEmployeesMutationVariables
    > = (props) => {
        const { projectId, data } = props ?? {};

        return updateProjectEmployees(projectId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/employees` },
        { operationId: 'update_project_employees', operationName: 'updateProjectEmployees' }
    );

    return customOptions;
};

export type UpdateProjectEmployeesMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmployeesHook>>>
>;
export type UpdateProjectEmployeesMutationBody = ProjectEmployeesUpdate;
export type UpdateProjectEmployeesMutationError = ErrorType<ErrorResponse>;
export type UpdateProjectEmployeesMutationVariables = { projectId: string; data: ProjectEmployeesUpdate };

/**
 * @summary Update Project Employees
 */
export const useUpdateProjectEmployees = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmployeesHook>>>,
            TError,
            UpdateProjectEmployeesMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmployeesHook>>>,
    TError,
    UpdateProjectEmployeesMutationVariables,
    TContext
> => {
    return useMutation(useUpdateProjectEmployeesMutationOptions(options), queryClient);
};
export const getUploadProjectLogoUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/logo`;
};

/**
 * @summary Upload Project Logo
 */
export const useUploadProjectLogoHook = (): ((
    projectId: string,
    uploadProjectLogoBody: UploadProjectLogoBody,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const customFetcher = useApiFetch();
    return (
        projectId: string,
        uploadProjectLogoBody: UploadProjectLogoBody,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        const formData = new FormData();
        formData.append(`file`, uploadProjectLogoBody.file);

        return customFetcher(getUploadProjectLogoUrl(projectId), {
            ...options,
            method: 'POST',
            body: formData,
            schema: Project,
        });
    };
};

export const getUploadProjectLogoMutationKey = () => ['uploadProjectLogo'] as const;

export const useUploadProjectLogoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUploadProjectLogoHook>>>,
        TError,
        UploadProjectLogoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUploadProjectLogoHook>>>,
    TError,
    UploadProjectLogoMutationVariables,
    TContext
> => {
    const mutationKey = getUploadProjectLogoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const uploadProjectLogo = useUploadProjectLogoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUploadProjectLogoHook>>>,
        UploadProjectLogoMutationVariables
    > = (props) => {
        const { projectId, data } = props ?? {};

        return uploadProjectLogo(projectId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/logo` },
        { operationId: 'upload_project_logo', operationName: 'uploadProjectLogo' }
    );

    return customOptions;
};

export type UploadProjectLogoMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUploadProjectLogoHook>>>
>;
export type UploadProjectLogoMutationBody = UploadProjectLogoBody;
export type UploadProjectLogoMutationError = ErrorType<ErrorResponse>;
export type UploadProjectLogoMutationVariables = { projectId: string; data: UploadProjectLogoBody };

/**
 * @summary Upload Project Logo
 */
export const useUploadProjectLogo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUploadProjectLogoHook>>>,
            TError,
            UploadProjectLogoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUploadProjectLogoHook>>>,
    TError,
    UploadProjectLogoMutationVariables,
    TContext
> => {
    return useMutation(useUploadProjectLogoMutationOptions(options), queryClient);
};
export const getDeleteProjectLogoUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/logo`;
};

/**
 * @summary Delete Project Logo
 */
export const useDeleteProjectLogoHook = (): ((
    projectId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const customFetcher = useApiFetch();
    return (projectId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteProjectLogoUrl(projectId), {
            ...options,
            method: 'DELETE',

            schema: Project,
        });
    };
};

export const getDeleteProjectLogoMutationKey = () => ['deleteProjectLogo'] as const;

export const useDeleteProjectLogoMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteProjectLogoHook>>>,
        TError,
        DeleteProjectLogoMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteProjectLogoHook>>>,
    TError,
    DeleteProjectLogoMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteProjectLogoMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteProjectLogo = useDeleteProjectLogoHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteProjectLogoHook>>>,
        DeleteProjectLogoMutationVariables
    > = (props) => {
        const { projectId } = props ?? {};

        return deleteProjectLogo(projectId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/logo` },
        { operationId: 'delete_project_logo', operationName: 'deleteProjectLogo' }
    );

    return customOptions;
};

export type DeleteProjectLogoMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteProjectLogoHook>>>
>;

export type DeleteProjectLogoMutationError = ErrorType<ErrorResponse>;
export type DeleteProjectLogoMutationVariables = { projectId: string };

/**
 * @summary Delete Project Logo
 */
export const useDeleteProjectLogo = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteProjectLogoHook>>>,
            TError,
            DeleteProjectLogoMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteProjectLogoHook>>>,
    TError,
    DeleteProjectLogoMutationVariables,
    TContext
> => {
    return useMutation(useDeleteProjectLogoMutationOptions(options), queryClient);
};
export const getGetProjectLogoUrl = (projectId: string | null) => {
    return `/workspace/projects/${projectId}/logo`;
};

/**
 * @summary Get Project Logo
 */
export const useGetProjectLogoHook = (): ((
    projectId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (projectId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetProjectLogoUrl(projectId), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetProjectLogoQueryKey = (projectId: string | null) => {
    return ['workspace', 'projects', projectId, 'logo'] as const;
};

export const useGetProjectLogoQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetProjectLogoQueryKey(projectId);

    const getProjectLogo = useGetProjectLogoHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>> = ({ signal }) =>
        getProjectLogo(projectId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: projectId !== null && projectId !== undefined, ...queryOptions },
        { projectId },
        {
            url: `/workspace/projects/${projectId}/logo`,
            operationId: 'get_project_logo',
            operationName: 'getProjectLogo',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProjectLogoQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>>;
export type GetProjectLogoQueryError = ErrorType<ErrorResponse>;

export function useGetProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Project Logo
 */

export function useGetProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetProjectLogoHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetProjectLogoQueryOptions(projectId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getCreatePublicLinkUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/public-link`;
};

/**
 * @summary Create Public Link
 */
export const useCreatePublicLinkHook = (): ((
    projectId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const customFetcher = useApiFetch();
    return (projectId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getCreatePublicLinkUrl(projectId), {
            ...options,
            method: 'POST',

            schema: Project,
        });
    };
};

export const getCreatePublicLinkMutationKey = () => ['createPublicLink'] as const;

export const useCreatePublicLinkMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreatePublicLinkHook>>>,
        TError,
        CreatePublicLinkMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreatePublicLinkHook>>>,
    TError,
    CreatePublicLinkMutationVariables,
    TContext
> => {
    const mutationKey = getCreatePublicLinkMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createPublicLink = useCreatePublicLinkHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreatePublicLinkHook>>>,
        CreatePublicLinkMutationVariables
    > = (props) => {
        const { projectId } = props ?? {};

        return createPublicLink(projectId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/public-link` },
        { operationId: 'create_public_link', operationName: 'createPublicLink' }
    );

    return customOptions;
};

export type CreatePublicLinkMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreatePublicLinkHook>>>
>;

export type CreatePublicLinkMutationError = ErrorType<ErrorResponse>;
export type CreatePublicLinkMutationVariables = { projectId: string };

/**
 * @summary Create Public Link
 */
export const useCreatePublicLink = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreatePublicLinkHook>>>,
            TError,
            CreatePublicLinkMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreatePublicLinkHook>>>,
    TError,
    CreatePublicLinkMutationVariables,
    TContext
> => {
    return useMutation(useCreatePublicLinkMutationOptions(options), queryClient);
};
export const getDeletePublicLinkUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/public-link`;
};

/**
 * @summary Delete Public Link
 */
export const useDeletePublicLinkHook = (): ((
    projectId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const customFetcher = useApiFetch();
    return (projectId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeletePublicLinkUrl(projectId), {
            ...options,
            method: 'DELETE',

            schema: Project,
        });
    };
};

export const getDeletePublicLinkMutationKey = () => ['deletePublicLink'] as const;

export const useDeletePublicLinkMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeletePublicLinkHook>>>,
        TError,
        DeletePublicLinkMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeletePublicLinkHook>>>,
    TError,
    DeletePublicLinkMutationVariables,
    TContext
> => {
    const mutationKey = getDeletePublicLinkMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deletePublicLink = useDeletePublicLinkHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeletePublicLinkHook>>>,
        DeletePublicLinkMutationVariables
    > = (props) => {
        const { projectId } = props ?? {};

        return deletePublicLink(projectId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/public-link` },
        { operationId: 'delete_public_link', operationName: 'deletePublicLink' }
    );

    return customOptions;
};

export type DeletePublicLinkMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeletePublicLinkHook>>>
>;

export type DeletePublicLinkMutationError = ErrorType<ErrorResponse>;
export type DeletePublicLinkMutationVariables = { projectId: string };

/**
 * @summary Delete Public Link
 */
export const useDeletePublicLink = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeletePublicLinkHook>>>,
            TError,
            DeletePublicLinkMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeletePublicLinkHook>>>,
    TError,
    DeletePublicLinkMutationVariables,
    TContext
> => {
    return useMutation(useDeletePublicLinkMutationOptions(options), queryClient);
};
export const getRenewClientEmailAddressUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}/email-address`;
};

/**
 * @summary Renew Client Email Address
 */
export const useRenewClientEmailAddressHook = (): ((
    clientId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const customFetcher = useApiFetch();
    return (clientId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getRenewClientEmailAddressUrl(clientId), {
            ...options,
            method: 'POST',

            schema: Client,
        });
    };
};

export const getRenewClientEmailAddressMutationKey = () => ['renewClientEmailAddress'] as const;

export const useRenewClientEmailAddressMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useRenewClientEmailAddressHook>>>,
        TError,
        RenewClientEmailAddressMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRenewClientEmailAddressHook>>>,
    TError,
    RenewClientEmailAddressMutationVariables,
    TContext
> => {
    const mutationKey = getRenewClientEmailAddressMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const renewClientEmailAddress = useRenewClientEmailAddressHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useRenewClientEmailAddressHook>>>,
        RenewClientEmailAddressMutationVariables
    > = (props) => {
        const { clientId } = props ?? {};

        return renewClientEmailAddress(clientId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}/email-address` },
        { operationId: 'renew_client_email_address', operationName: 'renewClientEmailAddress' }
    );

    return customOptions;
};

export type RenewClientEmailAddressMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useRenewClientEmailAddressHook>>>
>;

export type RenewClientEmailAddressMutationError = ErrorType<ErrorResponse>;
export type RenewClientEmailAddressMutationVariables = { clientId: string };

/**
 * @summary Renew Client Email Address
 */
export const useRenewClientEmailAddress = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useRenewClientEmailAddressHook>>>,
            TError,
            RenewClientEmailAddressMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useRenewClientEmailAddressHook>>>,
    TError,
    RenewClientEmailAddressMutationVariables,
    TContext
> => {
    return useMutation(useRenewClientEmailAddressMutationOptions(options), queryClient);
};
export const getUpdateClientEmailAddressUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}/email-address`;
};

/**
 * @summary Update Client Email Address
 */
export const useUpdateClientEmailAddressHook = (): ((
    clientId: string,
    emailAddressUpdate: EmailAddressUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Client>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        clientId: string,
        emailAddressUpdate: EmailAddressUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateClientEmailAddressUrl(clientId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(emailAddressUpdate),
            schema: Client,
        });
    };
};

export const getUpdateClientEmailAddressMutationKey = () => ['updateClientEmailAddress'] as const;

export const useUpdateClientEmailAddressMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientEmailAddressHook>>>,
        TError,
        UpdateClientEmailAddressMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientEmailAddressHook>>>,
    TError,
    UpdateClientEmailAddressMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateClientEmailAddressMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateClientEmailAddress = useUpdateClientEmailAddressHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateClientEmailAddressHook>>>,
        UpdateClientEmailAddressMutationVariables
    > = (props) => {
        const { clientId, data } = props ?? {};

        return updateClientEmailAddress(clientId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}/email-address` },
        { operationId: 'update_client_email_address', operationName: 'updateClientEmailAddress' }
    );

    return customOptions;
};

export type UpdateClientEmailAddressMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientEmailAddressHook>>>
>;
export type UpdateClientEmailAddressMutationBody = EmailAddressUpdate;
export type UpdateClientEmailAddressMutationError = ErrorType<ErrorResponse>;
export type UpdateClientEmailAddressMutationVariables = { clientId: string; data: EmailAddressUpdate };

/**
 * @summary Update Client Email Address
 */
export const useUpdateClientEmailAddress = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateClientEmailAddressHook>>>,
            TError,
            UpdateClientEmailAddressMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateClientEmailAddressHook>>>,
    TError,
    UpdateClientEmailAddressMutationVariables,
    TContext
> => {
    return useMutation(useUpdateClientEmailAddressMutationOptions(options), queryClient);
};
export const getRenewProjectEmailAddressUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/email-address`;
};

/**
 * @summary Renew Project Email Address
 */
export const useRenewProjectEmailAddressHook = (): ((
    projectId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const customFetcher = useApiFetch();
    return (projectId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getRenewProjectEmailAddressUrl(projectId), {
            ...options,
            method: 'POST',

            schema: Project,
        });
    };
};

export const getRenewProjectEmailAddressMutationKey = () => ['renewProjectEmailAddress'] as const;

export const useRenewProjectEmailAddressMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useRenewProjectEmailAddressHook>>>,
        TError,
        RenewProjectEmailAddressMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRenewProjectEmailAddressHook>>>,
    TError,
    RenewProjectEmailAddressMutationVariables,
    TContext
> => {
    const mutationKey = getRenewProjectEmailAddressMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const renewProjectEmailAddress = useRenewProjectEmailAddressHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useRenewProjectEmailAddressHook>>>,
        RenewProjectEmailAddressMutationVariables
    > = (props) => {
        const { projectId } = props ?? {};

        return renewProjectEmailAddress(projectId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/email-address` },
        { operationId: 'renew_project_email_address', operationName: 'renewProjectEmailAddress' }
    );

    return customOptions;
};

export type RenewProjectEmailAddressMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useRenewProjectEmailAddressHook>>>
>;

export type RenewProjectEmailAddressMutationError = ErrorType<ErrorResponse>;
export type RenewProjectEmailAddressMutationVariables = { projectId: string };

/**
 * @summary Renew Project Email Address
 */
export const useRenewProjectEmailAddress = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useRenewProjectEmailAddressHook>>>,
            TError,
            RenewProjectEmailAddressMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useRenewProjectEmailAddressHook>>>,
    TError,
    RenewProjectEmailAddressMutationVariables,
    TContext
> => {
    return useMutation(useRenewProjectEmailAddressMutationOptions(options), queryClient);
};
export const getUpdateProjectEmailAddressUrl = (projectId: string) => {
    return `/workspace/projects/${projectId}/email-address`;
};

/**
 * @summary Update Project Email Address
 */
export const useUpdateProjectEmailAddressHook = (): ((
    projectId: string,
    emailAddressUpdate: EmailAddressUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Project>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        projectId: string,
        emailAddressUpdate: EmailAddressUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateProjectEmailAddressUrl(projectId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(emailAddressUpdate),
            schema: Project,
        });
    };
};

export const getUpdateProjectEmailAddressMutationKey = () => ['updateProjectEmailAddress'] as const;

export const useUpdateProjectEmailAddressMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmailAddressHook>>>,
        TError,
        UpdateProjectEmailAddressMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmailAddressHook>>>,
    TError,
    UpdateProjectEmailAddressMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateProjectEmailAddressMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateProjectEmailAddress = useUpdateProjectEmailAddressHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmailAddressHook>>>,
        UpdateProjectEmailAddressMutationVariables
    > = (props) => {
        const { projectId, data } = props ?? {};

        return updateProjectEmailAddress(projectId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/projects/{projectId}/email-address` },
        { operationId: 'update_project_email_address', operationName: 'updateProjectEmailAddress' }
    );

    return customOptions;
};

export type UpdateProjectEmailAddressMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmailAddressHook>>>
>;
export type UpdateProjectEmailAddressMutationBody = EmailAddressUpdate;
export type UpdateProjectEmailAddressMutationError = ErrorType<ErrorResponse>;
export type UpdateProjectEmailAddressMutationVariables = { projectId: string; data: EmailAddressUpdate };

/**
 * @summary Update Project Email Address
 */
export const useUpdateProjectEmailAddress = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmailAddressHook>>>,
            TError,
            UpdateProjectEmailAddressMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateProjectEmailAddressHook>>>,
    TError,
    UpdateProjectEmailAddressMutationVariables,
    TContext
> => {
    return useMutation(useUpdateProjectEmailAddressMutationOptions(options), queryClient);
};
export const getListEmployeesUrl = (clientId: string | null) => {
    return `/workspace/clients/${clientId}/employees`;
};

/**
 * @summary List Employees
 */
export const useListEmployeesHook = (): ((
    clientId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Employee[]>) => {
    const customFetcher = useApiFetch();
    return (clientId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListEmployeesUrl(clientId), {
            ...options,
            method: 'GET',

            schema: zod.array(Employee),
        });
    };
};

export const getListEmployeesQueryKey = (clientId: string | null) => {
    return ['workspace', 'clients', clientId, 'employees'] as const;
};

export const useListEmployeesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListEmployeesQueryKey(clientId);

    const listEmployees = useListEmployeesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>> = ({ signal }) =>
        listEmployees(clientId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: clientId !== null && clientId !== undefined, ...queryOptions },
        { clientId },
        {
            url: `/workspace/clients/${clientId}/employees`,
            operationId: 'list_employees',
            operationName: 'listEmployees',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListEmployeesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>>;
export type ListEmployeesQueryError = ErrorType<ErrorResponse>;

export function useListEmployees<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>, TError, TData>> &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployees<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>, TError, TData>> &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployees<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Employees
 */

export function useListEmployees<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeesHook>>>, TError, TData>>;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListEmployeesQueryOptions(clientId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateEmployeeUrl = (employeeId: string) => {
    return `/workspace/employees/${employeeId}`;
};

/**
 * @summary Update Employee
 */
export const useUpdateEmployeeHook = (): ((
    employeeId: string,
    employeeUpdate: EmployeeUpdate,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<Employee>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        employeeId: string,
        employeeUpdate: EmployeeUpdate,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getUpdateEmployeeUrl(employeeId), {
            ...options,
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(employeeUpdate),
            schema: Employee,
        });
    };
};

export const getUpdateEmployeeMutationKey = () => ['updateEmployee'] as const;

export const useUpdateEmployeeMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateEmployeeHook>>>,
        TError,
        UpdateEmployeeMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateEmployeeHook>>>,
    TError,
    UpdateEmployeeMutationVariables,
    TContext
> => {
    const mutationKey = getUpdateEmployeeMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const updateEmployee = useUpdateEmployeeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateEmployeeHook>>>,
        UpdateEmployeeMutationVariables
    > = (props) => {
        const { employeeId, data } = props ?? {};

        return updateEmployee(employeeId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/employees/{employeeId}` },
        { operationId: 'update_employee', operationName: 'updateEmployee' }
    );

    return customOptions;
};

export type UpdateEmployeeMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateEmployeeHook>>>>;
export type UpdateEmployeeMutationBody = EmployeeUpdate;
export type UpdateEmployeeMutationError = ErrorType<ErrorResponse>;
export type UpdateEmployeeMutationVariables = { employeeId: string; data: EmployeeUpdate };

/**
 * @summary Update Employee
 */
export const useUpdateEmployee = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateEmployeeHook>>>,
            TError,
            UpdateEmployeeMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateEmployeeHook>>>,
    TError,
    UpdateEmployeeMutationVariables,
    TContext
> => {
    return useMutation(useUpdateEmployeeMutationOptions(options), queryClient);
};
export const getRemoveEmployeeUrl = (employeeId: string) => {
    return `/workspace/employees/${employeeId}`;
};

/**
 * @summary Remove Employee
 */
export const useRemoveEmployeeHook = (): ((
    employeeId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (employeeId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getRemoveEmployeeUrl(employeeId), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getRemoveEmployeeMutationKey = () => ['removeEmployee'] as const;

export const useRemoveEmployeeMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useRemoveEmployeeHook>>>,
        TError,
        RemoveEmployeeMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRemoveEmployeeHook>>>,
    TError,
    RemoveEmployeeMutationVariables,
    TContext
> => {
    const mutationKey = getRemoveEmployeeMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const removeEmployee = useRemoveEmployeeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useRemoveEmployeeHook>>>,
        RemoveEmployeeMutationVariables
    > = (props) => {
        const { employeeId } = props ?? {};

        return removeEmployee(employeeId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/employees/{employeeId}` },
        { operationId: 'remove_employee', operationName: 'removeEmployee' }
    );

    return customOptions;
};

export type RemoveEmployeeMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useRemoveEmployeeHook>>>>;

export type RemoveEmployeeMutationError = ErrorType<ErrorResponse>;
export type RemoveEmployeeMutationVariables = { employeeId: string };

/**
 * @summary Remove Employee
 */
export const useRemoveEmployee = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useRemoveEmployeeHook>>>,
            TError,
            RemoveEmployeeMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useRemoveEmployeeHook>>>,
    TError,
    RemoveEmployeeMutationVariables,
    TContext
> => {
    return useMutation(useRemoveEmployeeMutationOptions(options), queryClient);
};
export const getListEmployeeInvitationsUrl = (clientId: string | null) => {
    return `/workspace/clients/${clientId}/employee-invitations`;
};

/**
 * @summary List Employee Invitations
 */
export const useListEmployeeInvitationsHook = (): ((
    clientId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeInvitation[]>) => {
    const customFetcher = useApiFetch();
    return (clientId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getListEmployeeInvitationsUrl(clientId), {
            ...options,
            method: 'GET',

            schema: zod.array(EmployeeInvitation),
        });
    };
};

export const getListEmployeeInvitationsQueryKey = (clientId: string | null) => {
    return ['workspace', 'clients', clientId, 'employee-invitations'] as const;
};

export const useListEmployeeInvitationsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getListEmployeeInvitationsQueryKey(clientId);

    const listEmployeeInvitations = useListEmployeeInvitationsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>> = ({
        signal,
    }) => listEmployeeInvitations(clientId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: clientId !== null && clientId !== undefined, ...queryOptions },
        { clientId },
        {
            url: `/workspace/clients/${clientId}/employee-invitations`,
            operationId: 'list_employee_invitations',
            operationName: 'listEmployeeInvitations',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListEmployeeInvitationsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>
>;
export type ListEmployeeInvitationsQueryError = ErrorType<ErrorResponse>;

export function useListEmployeeInvitations<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployeeInvitations<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListEmployeeInvitations<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Employee Invitations
 */

export function useListEmployeeInvitations<
    TData = Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    clientId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useListEmployeeInvitationsHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useListEmployeeInvitationsQueryOptions(clientId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateEmployeeInvitationUrl = (clientId: string) => {
    return `/workspace/clients/${clientId}/employee-invitations`;
};

/**
 * @summary Create Employee Invitation
 */
export const useCreateEmployeeInvitationHook = (): ((
    clientId: string,
    employeeInvitationInput: EmployeeInvitationInput,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeInvitation>) => {
    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
        if (!h) return {};
        if (h instanceof Headers) return Object.fromEntries(h.entries());
        if (Symbol.iterator in h) {
            return Object.fromEntries(
                Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string])
            );
        }
        const headers: Record<string, string | readonly string[]> = {};
        for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
            if (value !== undefined) headers[name] = value;
        }
        return headers;
    };

    const customFetcher = useApiFetch();
    return (
        clientId: string,
        employeeInvitationInput: EmployeeInvitationInput,
        options?: Parameters<ReturnType<typeof useApiFetch>>[1]
    ) => {
        return customFetcher(getCreateEmployeeInvitationUrl(clientId), {
            ...options,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
            body: JSON.stringify(employeeInvitationInput),
            schema: EmployeeInvitation,
        });
    };
};

export const getCreateEmployeeInvitationMutationKey = () => ['createEmployeeInvitation'] as const;

export const useCreateEmployeeInvitationMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateEmployeeInvitationHook>>>,
        TError,
        CreateEmployeeInvitationMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateEmployeeInvitationHook>>>,
    TError,
    CreateEmployeeInvitationMutationVariables,
    TContext
> => {
    const mutationKey = getCreateEmployeeInvitationMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const createEmployeeInvitation = useCreateEmployeeInvitationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateEmployeeInvitationHook>>>,
        CreateEmployeeInvitationMutationVariables
    > = (props) => {
        const { clientId, data } = props ?? {};

        return createEmployeeInvitation(clientId, data, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/clients/{clientId}/employee-invitations` },
        { operationId: 'create_employee_invitation', operationName: 'createEmployeeInvitation' }
    );

    return customOptions;
};

export type CreateEmployeeInvitationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateEmployeeInvitationHook>>>
>;
export type CreateEmployeeInvitationMutationBody = EmployeeInvitationInput;
export type CreateEmployeeInvitationMutationError = ErrorType<ErrorResponse>;
export type CreateEmployeeInvitationMutationVariables = { clientId: string; data: EmployeeInvitationInput };

/**
 * @summary Create Employee Invitation
 */
export const useCreateEmployeeInvitation = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateEmployeeInvitationHook>>>,
            TError,
            CreateEmployeeInvitationMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateEmployeeInvitationHook>>>,
    TError,
    CreateEmployeeInvitationMutationVariables,
    TContext
> => {
    return useMutation(useCreateEmployeeInvitationMutationOptions(options), queryClient);
};
export const getSendEmployeeInvitationAgainUrl = (invitationId: string) => {
    return `/workspace/employee-invitations/${invitationId}/send`;
};

/**
 * @summary Send Employee Invitation Again
 */
export const useSendEmployeeInvitationAgainHook = (): ((
    invitationId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (invitationId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getSendEmployeeInvitationAgainUrl(invitationId), {
            ...options,
            method: 'POST',
        });
    };
};

export const getSendEmployeeInvitationAgainMutationKey = () => ['sendEmployeeInvitationAgain'] as const;

export const useSendEmployeeInvitationAgainMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useSendEmployeeInvitationAgainHook>>>,
        TError,
        SendEmployeeInvitationAgainMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useSendEmployeeInvitationAgainHook>>>,
    TError,
    SendEmployeeInvitationAgainMutationVariables,
    TContext
> => {
    const mutationKey = getSendEmployeeInvitationAgainMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const sendEmployeeInvitationAgain = useSendEmployeeInvitationAgainHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useSendEmployeeInvitationAgainHook>>>,
        SendEmployeeInvitationAgainMutationVariables
    > = (props) => {
        const { invitationId } = props ?? {};

        return sendEmployeeInvitationAgain(invitationId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/employee-invitations/{invitationId}/send` },
        { operationId: 'send_employee_invitation_again', operationName: 'sendEmployeeInvitationAgain' }
    );

    return customOptions;
};

export type SendEmployeeInvitationAgainMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useSendEmployeeInvitationAgainHook>>>
>;

export type SendEmployeeInvitationAgainMutationError = ErrorType<ErrorResponse>;
export type SendEmployeeInvitationAgainMutationVariables = { invitationId: string };

/**
 * @summary Send Employee Invitation Again
 */
export const useSendEmployeeInvitationAgain = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useSendEmployeeInvitationAgainHook>>>,
            TError,
            SendEmployeeInvitationAgainMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useSendEmployeeInvitationAgainHook>>>,
    TError,
    SendEmployeeInvitationAgainMutationVariables,
    TContext
> => {
    return useMutation(useSendEmployeeInvitationAgainMutationOptions(options), queryClient);
};
export const getDeleteEmployeeInvitationUrl = (invitationId: string) => {
    return `/workspace/employee-invitations/${invitationId}`;
};

/**
 * @summary Delete Employee Invitation
 */
export const useDeleteEmployeeInvitationHook = (): ((
    invitationId: string,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (invitationId: string, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getDeleteEmployeeInvitationUrl(invitationId), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getDeleteEmployeeInvitationMutationKey = () => ['deleteEmployeeInvitation'] as const;

export const useDeleteEmployeeInvitationMutationOptions = <
    TError = ErrorType<ErrorResponse>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteEmployeeInvitationHook>>>,
        TError,
        DeleteEmployeeInvitationMutationVariables,
        TContext
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteEmployeeInvitationHook>>>,
    TError,
    DeleteEmployeeInvitationMutationVariables,
    TContext
> => {
    const mutationKey = getDeleteEmployeeInvitationMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const deleteEmployeeInvitation = useDeleteEmployeeInvitationHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteEmployeeInvitationHook>>>,
        DeleteEmployeeInvitationMutationVariables
    > = (props) => {
        const { invitationId } = props ?? {};

        return deleteEmployeeInvitation(invitationId, requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/employee-invitations/{invitationId}` },
        { operationId: 'delete_employee_invitation', operationName: 'deleteEmployeeInvitation' }
    );

    return customOptions;
};

export type DeleteEmployeeInvitationMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteEmployeeInvitationHook>>>
>;

export type DeleteEmployeeInvitationMutationError = ErrorType<ErrorResponse>;
export type DeleteEmployeeInvitationMutationVariables = { invitationId: string };

/**
 * @summary Delete Employee Invitation
 */
export const useDeleteEmployeeInvitation = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteEmployeeInvitationHook>>>,
            TError,
            DeleteEmployeeInvitationMutationVariables,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteEmployeeInvitationHook>>>,
    TError,
    DeleteEmployeeInvitationMutationVariables,
    TContext
> => {
    return useMutation(useDeleteEmployeeInvitationMutationOptions(options), queryClient);
};
export const getGetEmployeeClientUrl = () => {
    return `/workspace/client`;
};

/**
 * @summary Get Employee Client
 */
export const useGetEmployeeClientHook = (): ((
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<EmployeeClientDetail>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetEmployeeClientUrl(), {
            ...options,
            method: 'GET',

            schema: EmployeeClientDetail,
        });
    };
};

export const getGetEmployeeClientQueryKey = () => {
    return ['workspace', 'client'] as const;
};

export const useGetEmployeeClientQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>, TError, TData>>;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetEmployeeClientQueryKey();

    const getEmployeeClient = useGetEmployeeClientHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>> = ({ signal }) =>
        getEmployeeClient({ signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        {},
        { url: `/workspace/client`, operationId: 'get_employee_client', operationName: 'getEmployeeClient' }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEmployeeClientQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>
>;
export type GetEmployeeClientQueryError = ErrorType<ErrorResponse>;

export function useGetEmployeeClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Employee Client
 */

export function useGetEmployeeClient<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetEmployeeClientQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getLeaveClientUrl = () => {
    return `/workspace/client/me`;
};

/**
 * @summary Leave Client
 */
export const useLeaveClientHook = (): ((options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getLeaveClientUrl(), {
            ...options,
            method: 'DELETE',
        });
    };
};

export const getLeaveClientMutationKey = () => ['leaveClient'] as const;

export const useLeaveClientMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useLeaveClientHook>>>, TError, void, TContext>;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useLeaveClientHook>>>, TError, void, TContext> => {
    const mutationKey = getLeaveClientMutationKey();
    const { mutation: mutationOptions, request: requestOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey }, request: undefined };

    const leaveClient = useLeaveClientHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useLeaveClientHook>>>, void> = () => {
        return leaveClient(requestOptions);
    };

    const customOptions = useMutationBehavior(
        { ...mutationOptions, mutationFn },
        { url: `/workspace/client/me` },
        { operationId: 'leave_client', operationName: 'leaveClient' }
    );

    return customOptions;
};

export type LeaveClientMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useLeaveClientHook>>>>;

export type LeaveClientMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Leave Client
 */
export const useLeaveClient = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useLeaveClientHook>>>,
            TError,
            void,
            TContext
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useLeaveClientHook>>>, TError, void, TContext> => {
    return useMutation(useLeaveClientMutationOptions(options), queryClient);
};
export const getGetEmployeeClientLogoUrl = () => {
    return `/workspace/client/logo`;
};

/**
 * @summary Get Employee Client Logo
 */
export const useGetEmployeeClientLogoHook = (): ((
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetEmployeeClientLogoUrl(), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetEmployeeClientLogoQueryKey = () => {
    return ['workspace', 'client', 'logo'] as const;
};

export const useGetEmployeeClientLogoQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(options?: {
    query?: Partial<
        UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>, TError, TData>
    >;
    request?: SecondParameter<ReturnType<typeof useApiFetch>>;
}) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetEmployeeClientLogoQueryKey();

    const getEmployeeClientLogo = useGetEmployeeClientLogoHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>> = ({ signal }) =>
        getEmployeeClientLogo({ signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, ...queryOptions },
        {},
        {
            url: `/workspace/client/logo`,
            operationId: 'get_employee_client_logo',
            operationName: 'getEmployeeClientLogo',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEmployeeClientLogoQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>
>;
export type GetEmployeeClientLogoQueryError = ErrorType<ErrorResponse>;

export function useGetEmployeeClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Employee Client Logo
 */

export function useGetEmployeeClientLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeClientLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetEmployeeClientLogoQueryOptions(options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}

export const getGetEmployeeProjectLogoUrl = (projectId: string | null) => {
    return `/workspace/client/projects/${projectId}/logo`;
};

/**
 * @summary Get Employee Project Logo
 */
export const useGetEmployeeProjectLogoHook = (): ((
    projectId: string | null,
    options?: Parameters<ReturnType<typeof useApiFetch>>[1]
) => Promise<void>) => {
    const customFetcher = useApiFetch();
    return (projectId: string | null, options?: Parameters<ReturnType<typeof useApiFetch>>[1]) => {
        return customFetcher(getGetEmployeeProjectLogoUrl(projectId), {
            ...options,
            method: 'GET',
        });
    };
};

export const getGetEmployeeProjectLogoQueryKey = (projectId: string | null) => {
    return ['workspace', 'client', 'projects', projectId, 'logo'] as const;
};

export const useGetEmployeeProjectLogoQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    }
) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetEmployeeProjectLogoQueryKey(projectId);

    const getEmployeeProjectLogo = useGetEmployeeProjectLogoHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>> = ({
        signal,
    }) => getEmployeeProjectLogo(projectId, { signal, ...requestOptions });

    const customOptions = useQueryBehavior(
        { queryKey, queryFn, enabled: projectId !== null && projectId !== undefined, ...queryOptions },
        { projectId },
        {
            url: `/workspace/client/projects/${projectId}/logo`,
            operationId: 'get_employee_project_logo',
            operationName: 'getEmployeeProjectLogo',
        }
    );

    return customOptions as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEmployeeProjectLogoQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>
>;
export type GetEmployeeProjectLogoQueryError = ErrorType<ErrorResponse>;

export function useGetEmployeeProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>, TError, TData>
        > &
            Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>, TError, TData>
        > &
            Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>
                >,
                'initialData'
            >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEmployeeProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Employee Project Logo
 */

export function useGetEmployeeProjectLogo<
    TData = Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>,
    TError = ErrorType<ErrorResponse>,
>(
    projectId: string | null,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetEmployeeProjectLogoHook>>>, TError, TData>
        >;
        request?: SecondParameter<ReturnType<typeof useApiFetch>>;
    },
    queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetEmployeeProjectLogoQueryOptions(projectId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return withQueryKey(query, queryOptions.queryKey);
}
